import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, Column, Cell, HeaderGroup } from 'react-table'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import DelayedRender from '@/components/hoc/DelayedRender'
import Pagination from '@/components/ui/pagination/Pagination'

import { AvailabilityFormValues } from './AvailabilityForm'
import CreateUnavailabilityDialog from './CreateUnavailabilityDialog'
import DeleteAvailabilityDialog from './DeleteAvailabilityDialog'
import UpdateAvailabilityDialog from './UpdateAvailabilityDialog'
import { AvailabilityMeetingPlace, Unavailability } from '@/core/models/meeting-link.model'
import { useDeleteUnavailabilityMutation } from '@/core/services/meeting-link'
import { StaffData } from './StaffSelectionTable'

const defaultRowsPerPageOptions = [5, 10, 15]

export type AvailabilityData = AvailabilityFormValues & {
  id: string
  unavailabilities?: Unavailability[]
}

export type AvailabilitiesTableProps = {
  data: AvailabilityData[]
  disableActions?: boolean
  isUpdating?: boolean
  loading?: boolean
  meetingLinkId?: string
  meetingLinkSpecialities: string[]
  onDeleteAvailability?: (availabilityId: string) => void
  onPageChange?: (newPage: number, rowsPerPage: number) => void
  onUpdateAvailability?: (values: AvailabilityData) => void
  rowsPerPageOptions?: number[]
  totalCount: number
  staffs: StaffData[]
}

export function AvailabilitiesTable(props: AvailabilitiesTableProps) {
  const {
    data,
    disableActions = false,
    isUpdating = false,
    loading = false,
    meetingLinkId,
    meetingLinkSpecialities,
    rowsPerPageOptions = defaultRowsPerPageOptions,
    onDeleteAvailability,
    onPageChange,
    onUpdateAvailability,
    totalCount,
    staffs,
  } = props
  const { t } = useTranslation()

  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions[0])
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const [deleteUnavailability] = useDeleteUnavailabilityMutation()

  const toggleExpanded = (id: string) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleDeleteUnavailability = (availabilityId: string, unavailabilityId: string) => {
    if (!meetingLinkId || !window.confirm(t('page.meetingLink.unavailability.delete.confirm'))) return
    deleteUnavailability({ meetingLinkId, availabilityId, unavailabilityId })
  }

  const formatUnavailabilityTime = (u: Unavailability) => {
    if (u.startTime == null || u.endTime == null) return t('page.meetingLink.unavailability.fullDay')
    const start = `${String(Math.floor(u.startTime / 60)).padStart(2, '0')}:${String(u.startTime % 60).padStart(2, '0')}`
    const end = `${String(Math.floor(u.endTime / 60)).padStart(2, '0')}:${String(u.endTime % 60).padStart(2, '0')}`
    return `${start} - ${end}`
  }

  const dayNames = useMemo(() => _.range(7).map((d) => dayjs().day(d).format('dd')), [])

  const columns = useMemo(() => {
    return [
      {
        Header: t('page.meetingLink.availabilitiesTable.header.specialities'),
        accessor: ({ specialities }) => _.truncate(specialities.join(', '), { length: 30 }),
        width: 'unset',
      },
      {
        Header: t('page.meetingLink.availabilitiesTable.header.meetingPlace'),
        accessor: 'meetingPlace',
        Cell: ({ value: meetingPlace, row: { original: availabilityData } }) => {
          switch (meetingPlace) {
            case AvailabilityMeetingPlace.AT_HOME:
              return t('page.meetingLink.availabilitiesTable.cell.meetingPlace.atHome')
            case AvailabilityMeetingPlace.VISIO_CONFERENCE:
              return t('page.meetingLink.availabilitiesTable.cell.meetingPlace.visioConference')
            case AvailabilityMeetingPlace.SECOND_OPINION:
              return t('page.meetingLink.availabilitiesTable.cell.meetingPlace.secondOpinion')
            default:
              return availabilityData.address?.description || ''
          }
        },
        width: 'unset',
      },
      {
        Header: t('page.meetingLink.availabilitiesTable.header.schedule'),
        accessor: 'recurrenceDays',
        Cell: ({ row: { original: availabilityData } }) => {
          if (availabilityData.isRecurrence) {
            const activeDays = availabilityData.recurrenceDays
              .filter((d) => d.periods.length > 0)
              .map((d) => dayNames[d.day])
            return activeDays.length > 0 ? activeDays.join(', ') : '—'
          }
          const count = availabilityData.scheduledDays?.length ?? 0
          return count > 0 ? `${count} ${t('page.meetingLink.availabilitiesTable.header.schedule').toLowerCase()}` : '—'
        },
        width: 'unset',
      },
      {
        Header: t('page.meetingLink.availabilitiesTable.header.avgMeetingDuration'),
        accessor: ({ avgMeetingDuration }) => `${avgMeetingDuration} min`,
        width: 'unset',
      },
      {
        Header: t('page.meetingLink.availabilitiesTable.header.payment'),
        accessor: 'paymentConfig',
        Cell: ({ row: { original: availabilityData } }) => {
          if (!availabilityData.paymentConfig?.requiresPayment) {
            return <Chip size="small" label="—" variant="outlined" />
          }
          return <Chip size="small" label={`${availabilityData.paymentConfig.basePrice ?? 0} €`} color="success" variant="outlined" />
        },
        width: 'unset',
      },
    ] as Column<AvailabilityData>[]
  }, [t, dayNames])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<AvailabilityData>({
    columns,
    data,
  })

  const emptyRows = Math.max(0, rowsPerPage - rows.length)

  const handleChangePage = (newPage: number, rowsPerPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage, rowsPerPage)
  }

  const handleDeleteAvailability = (avaialabilityId: string) => () => {
    onDeleteAvailability?.(avaialabilityId)
  }

  const handleUpdateAvailability = (availabilityId: string) => (values: AvailabilityFormValues) => {
    onUpdateAvailability?.({ id: availabilityId, ...values })
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    if (Math.ceil(totalCount / newRowsPerPage) >= page) onPageChange?.(page, newRowsPerPage)
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          {headerGroups.map((headerGroup: HeaderGroup<AvailabilityData>) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: HeaderGroup<AvailabilityData>) => {
                return (
                  <TableCell
                    {...column.getHeaderProps()}
                    align={column.align || 'left'}
                    scope="col"
                    sx={{ width: column.width, maxWidth: column.maxWidth, minWidth: column.minWidth, wordWrap: 'break-word' }}
                  >
                    {column.render('Header')}
                  </TableCell>
                )
              })}
              {!disableActions && <TableCell align="right">{t('common:table.header.action')}</TableCell>}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
          {loading && (
            <DelayedRender delay={300}>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            </DelayedRender>
          )}
          {rows.map((row) => {
            prepareRow(row)
            const unavailabilities = row.original.unavailabilities || []
            const isExpanded = expandedRows[row.original.id]
            return (
              <React.Fragment key={row.original.id}>
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell: Cell<AvailabilityData>) => (
                    <TableCell {...cell.getCellProps()} align={cell.column.align || 'left'}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                  {!disableActions && (
                    <TableCell key={`cell_${row.index}_actions`} align="right">
                      <Stack justifyContent="flex-end" direction="row" spacing={1}>
                        {meetingLinkId && (
                          <CreateUnavailabilityDialog meetingLinkId={meetingLinkId} availabilityId={row.original.id} />
                        )}
                        {unavailabilities.length > 0 && (
                          <Tooltip title={`${unavailabilities.length} blocked`}>
                            <IconButton size="small" onClick={() => toggleExpanded(row.original.id)}>
                              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <UpdateAvailabilityDialog
                          initialValues={row.original}
                          isSubmitting={isUpdating}
                          onSubmit={handleUpdateAvailability(row.original.id)}
                          specialities={meetingLinkSpecialities}
                          staffs={staffs}
                        />
                        <DeleteAvailabilityDialog onDelete={handleDeleteAvailability(row.original.id)} />
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} sx={{ py: 0, px: 2, bgcolor: '#fafafa' }}>
                      <Collapse in={isExpanded}>
                        <Box sx={{ py: 1 }}>
                          {unavailabilities.map((u) => (
                            <Stack key={u._id} direction="row" alignItems="center" spacing={2} sx={{ py: 0.5 }}>
                              <Chip
                                size="small"
                                label={formatUnavailabilityTime(u)}
                                color={u.startTime == null ? 'error' : 'warning'}
                                variant="outlined"
                              />
                              <Typography variant="body2">
                                {dayjs(u.startDate).format('DD/MM/YYYY')}
                                {u.startDate !== u.endDate && ` - ${dayjs(u.endDate).format('DD/MM/YYYY')}`}
                              </Typography>
                              {u.reason && (
                                <Typography variant="body2" color="text.secondary">
                                  {u.reason}
                                </Typography>
                              )}
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUnavailability(row.original.id, u._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ))}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            )
          })}
          {emptyRows > 0 && (
            <TableRow style={{ height: 63 * emptyRows }}>
              <TableCell colSpan={columns.length + 1} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
        <Pagination
          color="primary"
          itemsPerPageOptions={rowsPerPageOptions}
          shape="rounded"
          page={page}
          onPageChange={handleChangePage}
          onItemsPerPageChange={handleRowsPerPageChange}
          totalItems={totalCount}
        />
      </Box>
    </TableContainer>
  )
}

export default AvailabilitiesTable
